import {
    useEffect, useMemo, useState, type FormEvent, type MouseEvent,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { activePractitionerDetails } from 'src/graphql';
import { useNavigate } from 'react-router-dom';
import MainLayout from 'src/Components/Pages/MainLayout';
import {
    PrimaryButton, SecondaryButton, Skeleton,
} from '@luminalearning/components';
import { DocumentTextIcon } from '@heroicons/react/solid';
import UPDATE_API_HOOK_URL from 'src/graphql/mutations/updateApiHookUrl';
import GENERATE_API_KEY from 'src/graphql/mutations/generateApiKey';
import GET_API_HOOK_URLS from 'src/graphql/queries/apiHookUrls';
import TEST_API_PROJECTS from 'src/graphql/queries/testApiProjects';
import PROJECTS from 'src/graphql/queries/projects';
import GET_API_CREDENTIALS from 'src/graphql/queries/apiCredentials';
import ApiCredentialsSection from 'src/Components/Developer/ApiCredentialsSection';
import WebhookSection from 'src/Components/Developer/WebhookSection';
import ProjectListSection, { ProjectCardProject } from 'src/Components/Developer/ProjectListSection';

export type EnvironmentKey = 'live' | 'test';

type UrlValues = {
    exitRedirectUrl: string;
    completeRedirectUrl: string;
    completeNotificationHookUrl: string;
    pdfReadyNotificationHookUrl: string;
    pdfDownloadHookUrl: string;
};

type ApiCredentials = {
    clientId: string;
    clientSecret: string;
    error: string | null;
};

type ApiCredentialsSource = {
    clientId?: string | null;
    clientSecret?: string | null;
    error?: string | null;
} | null;

const emptyCredentials: ApiCredentials = { clientId: '', clientSecret: '', error: null };

type SaveStatus = {
    tone: 'Success' | 'Error';
    message: string;
    isShow: boolean;
};

const emptyUrls: UrlValues = {
    exitRedirectUrl: '',
    completeRedirectUrl: '',
    completeNotificationHookUrl: '',
    pdfReadyNotificationHookUrl: '',
    pdfDownloadHookUrl: '',
};

const PageSkeleton = () => (
    <div className='pb-20'>
        <div className='mb-4'>
            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={40} width={280} />
        </div>
        <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={20} width={520} />
        <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={20} width={380} className='mt-2' />
        <div className='mt-8 flex flex-row gap-x-4'>
            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={44} width={200} />
            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={44} width={200} />
        </div>
        <div className='mt-8 w-full xl:pr-4 2xl:pr-20 3xl:pr-2'>
            <div
                className='mt-6 border-1.5 border-card-normal rounded-lg p-6 bg-white shadow-card'
            >
                <div className='flex flex-col gap-y-3'>
                    <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={22} width={320} />
                    <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={46} width="100%" />
                    <div className="py-2"></div>
                    <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={22} width={320} />
                    <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={46} width="100%" />
                    <div className='flex justify-end'>
                        <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={40} width={120} />
                    </div>
                </div>
            </div>
        </div>
        <div className='mt-8 w-full xl:pr-4 2xl:pr-20 3xl:pr-2'>
            {[0, 1, 2, 3, 4].map((index) => (
                <div
                    key={index}
                    className='mt-6 border-1.5 border-card-normal rounded-lg p-6 bg-white shadow-card'
                >
                    <div className='flex flex-col gap-y-3'>
                        <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={22} width={320} />
                        <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={18} width={520} />
                        <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={46} width='100%' />
                        <div className='flex justify-end'>
                            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={40} width={120} />
                        </div>
                    </div>
                </div>
            ))}
        </div>
        <div className='mt-12'>
            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={28} width={180} />
            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={18} width={360} className='mt-2' />
            <div className='mt-6 grid gap-4 lg:grid-cols-2'>
                {[0, 1].map((index) => (
                    <div
                        key={`project-skeleton-${index}`}
                        className='border-1.5 border-card-normal rounded-lg p-6 bg-white shadow-card'
                    >
                        <div className='flex flex-col gap-y-3'>
                            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={22} width={220} />
                            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={16} width={140} />
                            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={18} width={280} />
                            <Skeleton baseColor='#d7d7d7' highlightColor='#c8c8c8' height={16} width={160} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const Developer = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const practitionerDetails = useReactiveVar(activePractitionerDetails);
    const [activeEnvironmentTab, setActiveEnvironmentTab] = useState<EnvironmentKey>('test');
    const [liveValues, setLiveValues] = useState<UrlValues>(emptyUrls);
    const [testValues, setTestValues] = useState<UrlValues>(emptyUrls);
    const [savingKeys, setSavingKeys] = useState<Record<string, boolean>>({});
    const [statusByKey, setStatusByKey] = useState<Record<string, SaveStatus | undefined>>({});
    const [updateApiHookUrl] = useMutation(UPDATE_API_HOOK_URL);
    const [generateApiKey, { loading: isGeneratingApiKey }] = useMutation(GENERATE_API_KEY);
    const [hasLoadedUrls, setHasLoadedUrls] = useState(false);
    const [testCredentials, setTestCredentials] = useState<ApiCredentials>(emptyCredentials);
    const [liveCredentials, setLiveCredentials] = useState<ApiCredentials>(emptyCredentials);
    const [hasLoadedCredentials, setHasLoadedCredentials] = useState(false);
    const [testProjectsPage, setTestProjectsPage] = useState(1);
    const [liveProjectsPage, setLiveProjectsPage] = useState(1);

    const profileId = practitionerDetails?.id ?? null;
    const testProjectsPagination = useMemo(
        () => ({ isEnabled: true, perPage: 10, page: testProjectsPage }),
        [testProjectsPage]
    );
    const liveProjectsPagination = useMemo(
        () => ({ isEnabled: true, perPage: 10, page: liveProjectsPage }),
        [liveProjectsPage]
    );
    const { data: apiHookUrlsData, loading: apiHookUrlsLoading } = useQuery(GET_API_HOOK_URLS, {
        variables: profileId ? { profileId } : undefined,
        skip: !profileId,
        fetchPolicy: 'network-only',
    });
    const { data: testProjectsData, loading: testProjectsLoading } = useQuery(TEST_API_PROJECTS, {
        variables: profileId ? { profileId, pagination: testProjectsPagination } : undefined,
        skip: !profileId,
        fetchPolicy: 'network-only',
    });
    const { data: liveProjectsData, loading: liveProjectsLoading } = useQuery(PROJECTS, {
        variables: profileId ? {
            profileId,
            pagination: liveProjectsPagination,
            isAtsProjects: false,
        } : undefined,
        skip: !profileId,
        fetchPolicy: 'network-only',
    });
    const { data: testCredentialsData, loading: testCredentialsLoading } = useQuery(GET_API_CREDENTIALS, {
        variables: profileId ? { profileId, environment: 'test' } : undefined,
        skip: !profileId,
        fetchPolicy: 'network-only',
    });
    const { data: liveCredentialsData, loading: liveCredentialsLoading } = useQuery(GET_API_CREDENTIALS, {
        variables: profileId ? { profileId, environment: 'production' } : undefined,
        skip: !profileId,
        fetchPolicy: 'network-only',
    });

    const activeValues = activeEnvironmentTab === 'live' ? liveValues : testValues;
    const activeCredentials = activeEnvironmentTab === 'live' ? liveCredentials : testCredentials;
    const isCredentialsLoading = !hasLoadedCredentials || isGeneratingApiKey;
    const testProjects = useMemo<ProjectCardProject[]>(
        () => testProjectsData?.testApiProjects?.projects ?? [],
        [testProjectsData]
    );
    const liveProjects = useMemo<ProjectCardProject[]>(
        () => liveProjectsData?.projects?.projects ?? [],
        [liveProjectsData]
    );
    const testProjectsPaginationInfo = useMemo(() => {
        const info = testProjectsData?.testApiProjects?.paginationInfo;
        const currentPage = info?.currentPage ?? testProjectsPage;
        const maxPage = Math.max(info?.maxPage ?? 1, currentPage);
        const previousPage = info?.previousPage ?? null;
        const nextPage = currentPage < maxPage ? currentPage + 1 : null;

        return {
            currentPage,
            maxPage,
            previousPage,
            nextPage,
        };
    }, [testProjectsData, testProjectsPage]);
    const liveProjectsPaginationInfo = useMemo(() => {
        const info = liveProjectsData?.projects?.paginationInfo;

        const currentPage = info?.currentPage ?? liveProjectsPage;
        const maxPage = Math.max(info?.maxPage ?? 1, currentPage);
        const previousPage = info?.previousPage ?? null;
        const nextPage = currentPage < maxPage ? currentPage + 1 : null;

        return {
            currentPage,
            maxPage,
            previousPage,
            nextPage,
        };
    }, [liveProjectsData, liveProjectsPage]);
    const activeProjects = activeEnvironmentTab === 'test' ? testProjects : liveProjects;
    const showProjectSkeleton = (activeEnvironmentTab === 'test' && testProjectsLoading)
        || (activeEnvironmentTab === 'live' && liveProjectsLoading);

    useEffect(() => {
        document.title = intl.formatMessage({ id: 'LP_DEVELOPER_DASHBOARD_TITLE', defaultMessage: 'Developer Dashboard' });
    }, [intl]);

    useEffect(() => {
        if (practitionerDetails !== null && practitionerDetails.isApiEnabled !== true) {
            navigate('/');
        }
    }, [practitionerDetails, navigate]);

    const handleTestProjectsPageChange = (page: number) => {
        const safePage = Math.min(Math.max(page, 1), testProjectsPaginationInfo.maxPage);
        if (safePage !== testProjectsPage) {
            setTestProjectsPage(safePage);
        }
    };
    const handleLiveProjectsPageChange = (page: number) => {
        const safePage = Math.min(Math.max(page, 1), liveProjectsPaginationInfo.maxPage);
        if (safePage !== liveProjectsPage) {
            setLiveProjectsPage(safePage);
        }
    };

    const handleGenerateApiKey = async () => {
        if (!profileId) return;
        const environment = activeEnvironmentTab === 'live' ? 'production' : 'test';
        const setCredentials = activeEnvironmentTab === 'live' ? setLiveCredentials : setTestCredentials;
        try {
            const { data } = await generateApiKey({
                variables: { profileId, environment },
            });
            const result = data?.generateApiKey;
            if (result) {
                setCredentials({
                    clientId: result.clientId ?? '',
                    clientSecret: result.clientSecret ?? '',
                    error: result.error ?? null,
                });
            }
        } catch {
            setCredentials((prev) => ({ ...prev, error: 'server_error' }));
        }
    };

    useEffect(() => {
        const maxPage = testProjectsData?.testApiProjects?.paginationInfo?.maxPage;
        if (typeof maxPage === 'number' && maxPage > 0 && testProjectsPage > maxPage) {
            setTestProjectsPage(maxPage);
        }
    }, [testProjectsData, testProjectsPage]);

    useEffect(() => {
        const maxPage = liveProjectsData?.projects?.paginationInfo?.maxPage;
        if (typeof maxPage === 'number' && maxPage > 0 && liveProjectsPage > maxPage) {
            setLiveProjectsPage(maxPage);
        }
    }, [liveProjectsData, liveProjectsPage]);

    const fields = useMemo(() => ([
        {
            key: 'exitRedirectUrl' as keyof UrlValues,
            urlType: 'exit-redirect',
            labelMessage: {
                id: 'LP_DEVELOPER_EXIT_REDIRECT_URL',
                defaultMessage: 'Questionnaire exit redirect URL',
            },
            descriptionMessage: {
                id: 'LP_DEVELOPER_EXIT_REDIRECT_URL_DESC',
                defaultMessage: 'Where users are sent if they exit the questionnaire before completion.',
            },
            placeholder: 'https://example.com/exit',
        },
        {
            key: 'completeRedirectUrl' as keyof UrlValues,
            urlType: 'questionnaire-complete',
            labelMessage: {
                id: 'LP_DEVELOPER_COMPLETE_REDIRECT_URL',
                defaultMessage: 'Questionnaire complete redirect URL',
            },
            descriptionMessage: {
                id: 'LP_DEVELOPER_COMPLETE_REDIRECT_URL_DESC',
                defaultMessage: 'Where users are sent immediately after completing the questionnaire.',
            },
            placeholder: 'https://example.com/complete',
        },
        {
            key: 'completeNotificationHookUrl' as keyof UrlValues,
            urlType: 'questionnaire-complete-notification',
            labelMessage: {
                id: 'LP_DEVELOPER_COMPLETE_NOTIFICATION_HOOK_URL',
                defaultMessage: 'Questionnaire complete notification hook URL',
            },
            descriptionMessage: {
                id: 'LP_DEVELOPER_COMPLETE_NOTIFICATION_HOOK_URL_DESC',
                defaultMessage: 'Webhook triggered when a questionnaire is completed. Sent via POST request.',
            },
            placeholder: 'https://example.com/hooks/complete',
        },
        {
            key: 'pdfReadyNotificationHookUrl' as keyof UrlValues,
            urlType: 'portrait-notification',
            labelMessage: {
                id: 'LP_DEVELOPER_PDF_READY_NOTIFICATION_HOOK_URL',
                defaultMessage: 'PDF ready notification hook URL',
            },
            descriptionMessage: {
                id: 'LP_DEVELOPER_PDF_READY_NOTIFICATION_HOOK_URL_DESC',
                defaultMessage: 'Webhook triggered when the PDF portrait is ready. Sent via POST request.',
            },
            placeholder: 'https://example.com/hooks/pdf-ready',
        },
        {
            key: 'pdfDownloadHookUrl' as keyof UrlValues,
            urlType: 'pdf-hook',
            labelMessage: {
                id: 'LP_DEVELOPER_PDF_DOWNLOAD_HOOK_URL',
                defaultMessage: 'PDF portrait hook URL',
            },
            descriptionMessage: {
                id: 'LP_DEVELOPER_PDF_DOWNLOAD_HOOK_URL_DESC',
                defaultMessage: 'Webhook triggered when the PDF portrait is ready, it will sent the PDF portrait. Sent via POST request.',
            },
            placeholder: 'https://example.com/hooks/pdf-download',
        },
    ]), []);

    const updateValue = (environment: EnvironmentKey, key: keyof UrlValues, value: string) => {
        const setValues = environment === 'live' ? setLiveValues : setTestValues;
        setValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSubmit = async (
        event: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>,
        environment: EnvironmentKey,
        key: keyof UrlValues
    ) => {
        event.preventDefault();
        event.stopPropagation();
        const values = environment === 'live' ? liveValues : testValues;
        const submittedValue = values[key];
        const statusKey = `${environment}-${String(key)}`;
        const environmentValue = environment === 'live' ? 'production' : 'test';

        if (submittedValue.trim().length === 0) {
            return;
        }

        if (!practitionerDetails?.id) {
            setStatusByKey((prev) => ({
                ...prev,
                [statusKey]: {
                    tone: 'Error',
                    message: intl.formatMessage({
                        id: 'LP_DEVELOPER_MISSING_PROFILE',
                        defaultMessage: 'Unable to save: missing practitioner profile.',
                    }),
                    isShow: true,
                },
            }));

            setTimeout(() => {
                setStatusByKey((prev) => ({
                    ...prev,
                    [statusKey]: undefined,
                }));
            }, 1500);
            return;
        }

        const field = fields.find((item) => item.key === key);
        if (!field) {
            return;
        }

        setSavingKeys((prev) => ({ ...prev, [statusKey]: true }));
        setStatusByKey((prev) => ({
            ...prev,
            [statusKey]: undefined,
        }));

        try {
            const { data } = await updateApiHookUrl({
                variables: {
                    profileId: practitionerDetails.id,
                    urlType: field.urlType,
                    url: submittedValue,
                    environment: environmentValue,
                },
            });

            const response = data?.updateApiHookUrl;
            if (response === 'SUCCESS') {
                setStatusByKey((prev) => ({
                    ...prev,
                    [statusKey]: {
                        tone: 'Success',
                        message: intl.formatMessage({
                            id: 'LP_DEVELOPER_SAVE_SUCCESS',
                            defaultMessage: 'Saved successfully.',
                        }),
                        isShow: true,
                    },
                }));
                return;
            }

            const errorMessage = response === 'UNAUTHORIZED'
                ? intl.formatMessage({
                    id: 'LP_DEVELOPER_SAVE_UNAUTHORIZED',
                    defaultMessage: 'You are not authorized to update this URL.',
                })
                : intl.formatMessage({
                    id: 'LP_DEVELOPER_SAVE_FAILED',
                    defaultMessage: 'Failed to save. Please try again.',
                });

            setStatusByKey((prev) => ({
                ...prev,
                [statusKey]: {
                    tone: 'Error',
                    message: errorMessage,
                    isShow: true,
                },
            }));
        } catch (error) {
            setStatusByKey((prev) => ({
                ...prev,
                [statusKey]: {
                    tone: 'Error',
                    message: intl.formatMessage({
                        id: 'LP_DEVELOPER_SAVE_FAILED',
                        defaultMessage: 'Failed to save. Please try again.',
                    }),
                    isShow: true,
                },
            }));
        } finally {
            setTimeout(() => {
                setStatusByKey((prev) => ({
                    ...prev,
                    [statusKey]: undefined,
                }));
            }, 1500);
            setSavingKeys((prev) => { const { [statusKey]: _, ...rest } = prev; return rest; });
        }
    };

    useEffect(() => {
        setHasLoadedUrls(false);
        setLiveValues(emptyUrls);
        setTestValues(emptyUrls);
        setHasLoadedCredentials(false);
        setTestCredentials(emptyCredentials);
        setLiveCredentials(emptyCredentials);
        setTestProjectsPage(1);
        setLiveProjectsPage(1);
    }, [profileId]);

    useEffect(() => {
        if (!profileId || apiHookUrlsLoading || hasLoadedUrls) {
            return;
        }

        const response = apiHookUrlsData?.getApiHookUrls;

        const mapResponseToValues = (environment?: {
            pdfDownloadUrl?: string | null;
            pdfReadyNotificationUrl?: string | null;
            questionnaireCompleteNotificationUrl?: string | null;
            questionnaireCompleteUrl?: string | null;
            questionnaireExitUrl?: string | null;
        } | null) => ({
            exitRedirectUrl: environment?.questionnaireExitUrl ?? '',
            completeRedirectUrl: environment?.questionnaireCompleteUrl ?? '',
            completeNotificationHookUrl: environment?.questionnaireCompleteNotificationUrl ?? '',
            pdfReadyNotificationHookUrl: environment?.pdfReadyNotificationUrl ?? '',
            pdfDownloadHookUrl: environment?.pdfDownloadUrl ?? '',
        });

        setLiveValues(mapResponseToValues(response?.production));
        setTestValues(mapResponseToValues(response?.test));
        setHasLoadedUrls(true);
    }, [apiHookUrlsData, apiHookUrlsLoading, hasLoadedUrls, profileId]);

    useEffect(() => {
        if (!profileId || testCredentialsLoading || liveCredentialsLoading || hasLoadedCredentials) {
            return;
        }
        const mapCredentials = (creds?: ApiCredentialsSource): ApiCredentials => ({
            clientId: creds?.clientId ?? '',
            clientSecret: creds?.clientSecret ?? '',
            error: creds?.error ?? null,
        });
        setTestCredentials(mapCredentials(testCredentialsData?.getApiCredentials));
        setLiveCredentials(mapCredentials(liveCredentialsData?.getApiCredentials));
        setHasLoadedCredentials(true);
    }, [
        testCredentialsData,
        liveCredentialsData,
        testCredentialsLoading,
        liveCredentialsLoading,
        hasLoadedCredentials,
        profileId,
    ]);

    return (
        <MainLayout>
            {(!profileId || apiHookUrlsLoading || !hasLoadedUrls) ? <PageSkeleton /> : (
                <div className='pb-20'>
                    <h1 className='text-headline-lg font-bold' style={{ lineHeight: '45px' }}>
                        <FormattedMessage id="LP_DEVELOPER_DASHBOARD_TITLE" defaultMessage="Developer Dashboard" />
                    </h1>
                    <div className='mt-8 flex flex-row gap-x-4' role='tablist'>
                        {([
                            { key: 'test', label: intl.formatMessage({ id: 'LP_TEST_ENVIRONMENT', defaultMessage: 'Test environment' }) },
                            { key: 'live', label: intl.formatMessage({ id: 'LP_LIVE_ENVIRONMENT', defaultMessage: 'Live environment' }) },
                        ] as const).map((tab) => (
                            activeEnvironmentTab === tab.key ? (
                                <PrimaryButton
                                    key={tab.key}
                                    type='button'
                                    text={tab.label}
                                    onClick={() => setActiveEnvironmentTab(tab.key)}
                                    className='px-6 py-3 rounded-full text-label-reg font-bold'
                                />
                            ) : (
                                <SecondaryButton
                                    key={tab.key}
                                    type='button'
                                    className='px-6 py-3 rounded-full text-label-reg font-bold'
                                    onClick={() => setActiveEnvironmentTab(tab.key)}
                                    text={tab.label}
                                />
                            )
                        ))}
                    </div>
                    <div className='mt-4'>
                        <button
                            type='button'
                            onClick={() => navigate('/developer/documentation')}
                            className='flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold'
                            style={{
                                border: '1.5px solid #E2E2E2',
                                background: '#ffffff',
                                cursor: 'pointer',
                                color: '#374151',
                            }}
                        >
                            <DocumentTextIcon style={{ width: 18, height: 18, color: '#6b7280' }} />
                            <FormattedMessage id='LP_API_DOCUMENTATION' defaultMessage='API Documentation' />
                        </button>
                    </div>
                    <ApiCredentialsSection
                        activeCredentials={activeCredentials}
                        isCredentialsLoading={isCredentialsLoading}
                        onGenerateApiKey={handleGenerateApiKey}
                        isGeneratingApiKey={isGeneratingApiKey}
                    />
                    <WebhookSection
                        fields={fields}
                        activeEnvironmentTab={activeEnvironmentTab}
                        activeValues={activeValues}
                        statusByKey={statusByKey}
                        savingKeys={savingKeys}
                        onSubmit={handleSubmit}
                        onUpdateValue={updateValue}
                    />
                    <ProjectListSection
                        activeEnvironmentTab={activeEnvironmentTab}
                        activeProjects={activeProjects}
                        isLoading={showProjectSkeleton}
                        pagination={activeEnvironmentTab === 'test'
                            ? testProjectsPaginationInfo : liveProjectsPaginationInfo}
                        onPageChange={activeEnvironmentTab === 'test'
                            ? handleTestProjectsPageChange : handleLiveProjectsPageChange}
                        productQualifications={practitionerDetails?.productQualifications}
                        profileId={profileId}
                    />
                </div>
            )}
        </MainLayout>
    );
};

export default Developer;
